import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { HttpEikosClientService } from "./http-eikos-client.service";
import type { EikosEmitPolicyRequest, EikosEmitPolicyResponse } from "./eikos-policy-emission.types";

export interface EikosBrand {
  Marca: number;
  Descripcion: string;
  vehicle_type: string;
  year: number;
}

export interface EikosSubbrand {
  SubMarca: number;
  Descripcion: string;
  year: number;
  brand_id: number;
  brand_name: string;
}

export interface EikosVersion {
  ClaveInterna: string;
  Descripcion: string;
  year: number;
  brand_id: number;
  brand_name: string;
  subbrand_id: number;
  subbrand_name: string;
}

export interface EikosQuoteAllResponse {
  status: string;
  data?: unknown[];
  errors?: string[];
}

/**
 * Service for Eikos insurance broker API (MG brand).
 * Endpoints: POST brands, POST subbrands, POST versions, POST quote_all.
 */
@Injectable({
  providedIn: "root",
})
export class EikosService {
  private readonly basePath = "/api/eikos_api_service/v1/insurance";

  constructor(private http: HttpEikosClientService) {}

  getBrands(year: number): Observable<EikosBrand[]> {
    return this.http.post(`${this.basePath}/brands`, { year }).pipe(
      map((res: { status?: string; data?: EikosBrand[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  getSubbrands(year: number, brand_id: number, brand_name: string): Observable<EikosSubbrand[]> {
    return this.http.post(`${this.basePath}/subbrands`, { year, brand_id, brand_name }).pipe(
      map((res: { status?: string; data?: EikosSubbrand[] }) => res?.data ?? []),
      catchError(() => of([]))
    );
  }

  getVersions(
    year: number,
    brand_id: number,
    brand_name: string,
    subbrand_id: number,
    subbrand_name: string
  ): Observable<EikosVersion[]> {
    return this.http
      .post(`${this.basePath}/versions`, {
        year,
        brand_id,
        brand_name,
        subbrand_id,
        subbrand_name,
      })
      .pipe(
        map((res: { status?: string; data?: EikosVersion[] }) => res?.data ?? []),
        catchError(() => of([]))
      );
  }

  quoteAll(payload: { quote: Record<string, unknown> }): Observable<EikosQuoteAllResponse> {
    return this.http.post(`${this.basePath}/quote_all`, payload).pipe(
      map((res: EikosQuoteAllResponse) => res ?? { status: "ERROR", data: [], errors: [] }),
      catchError(() => of({ status: "ERROR", data: [], errors: ["Request failed"] }))
    );
  }

  /**
   * POST /api/v1/insurance/emit_policy
   */
  emitPolicyEikos(payload: EikosEmitPolicyRequest): Observable<EikosEmitPolicyResponse> {
    return this.http
      .post(`${this.basePath}/emit`, { emit: payload })
      .pipe(map((res: unknown) => this.normalizeEmitResponse(res as Record<string, unknown>)));
  }

  private normalizeEmitResponse(res: Record<string, unknown>): EikosEmitPolicyResponse {
    const data = (res?.data as Record<string, unknown>) ?? res;
    return {
      ID_COTIZACION: Number(data?.ID_COTIZACION ?? data?.id_cotizacion ?? 0),
      POLIZA: String(data?.POLIZA ?? data?.poliza ?? ""),
      POLIZA_URL: String(data?.POLIZA_URL ?? data?.poliza_url ?? ""),
      VIGENCIA_INI: String(data?.VIGENCIA_INI ?? data?.vigencia_ini ?? ""),
      VIGENCIA_FIN: String(data?.VIGENCIA_FIN ?? data?.vigencia_fin ?? ""),
    };
  }
}
